import React from 'react'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  totalItems: number
  pageSize: number
  onPageChange: (page: number) => void
  className?: string
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  className = '',
}) => {
  // Don't render if there's only one page or no items
  if (totalPages <= 1 || totalItems === 0) {
    return null
  }

  const startItem = (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, totalItems)

  return (
    <div className={`flex items-center justify-between ${className}`}>
      <div className='text-sm text-gray-500'>
        Showing {startItem} to {endItem} of {totalItems.toLocaleString()}{' '}
        entries
      </div>

      <div className='flex items-center gap-2'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className='shadow-none'
        >
          <ChevronLeft className='text-gray-400' size={16} />
        </Button>

        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const pageNum = i + 1
          return (
            <Button
              key={pageNum}
              variant={currentPage === pageNum ? 'default' : 'outline'}
              size='sm'
              onClick={() => onPageChange(pageNum)}
              className='w-8 shadow-none'
            >
              {pageNum}
            </Button>
          )
        })}

        {totalPages > 5 && (
          <>
            <span className='text-gray-400'>...</span>
            <Button
              variant={currentPage === totalPages ? 'default' : 'outline'}
              size='sm'
              onClick={() => onPageChange(totalPages)}
              className='w-8 shadow-none'
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant='outline'
          size='sm'
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className='shadow-none'
        >
          <ChevronRight className='text-gray-400' size={16} />
        </Button>
      </div>
    </div>
  )
}

export default Pagination
